// c021 지역3·4 몹 분위기 VFX: 서리 묘지 = 발밑 서리 안개 + 얼음 반짝임 / 처치 시 얼음 파편이 튀는 '산산조각',
// 가라앉은 사원 = 갓·등에서 피어오르는 빛나는 포자 + 발밑 독안개 / 처치 시 포자 구름 '훅'.
// C2 황금 모래 폐도 = 발밑을 쓸고 가는 모래 바람 + 금빛 티끌 / 처치 시 금빛 모래가 흩어져 흘러내림(sandBurst),
// 심연의 성채 = 발밑 그림자 연기 + 떠오르는 보라 불티 / 처치 시 보라 파편이 한 점으로 빨려 들어감(voidBurst).
// 미리 할당한 풀 세 개(안개 = 부드러운 원판 일반 블렌딩, 반짝임·포자 = 가산 점, 파편 = 마름모 조각)를 CPU로 갱신한다(InstancedMesh 3개, 드로우콜 3).
// 프레임 루프에서 객체를 만들지 않는다. 화면 섬광·화면 흔들림이 없다(FlashLimiter 예산을 쓰지 않는다).
// 예산(품질 particleScale 1 기준, 0.5 단계에서 절반): 오라는 카메라 22m 안 살아 있는 몹만, 전체 안개 ≤ 40·반짝임/포자 ≤ 56 살아 있음,
// 오라 생성 ≤ 초당 16개(몹 사이 순번). 처치 폭발 한 번 = 서리 파편 10 + 반짝임 6 + 안개 3 / 독 포자 10 + 안개 5,
// 한 틱 처치 폭발 ≤ 3번(대량 처치는 나머지를 건너뛴다), 보스 처치는 파편·반짝임만 3배(안개는 일반 수·알파 0.16). 모든 크기는 몸 둘레(≤ 1.4m)라 플레이 영역을 덮지 않는다.
import * as THREE from 'three/webgpu';
import { attribute, float, length, smoothstep, uv, vec2 } from 'three/tsl';
import { MobState, type GameView } from '../shared/types';
import { Ev, type GameEvent } from '../shared/events';
import { REGION_LOOK, type RegionLook } from '../render/actors/regionAttach';
import { TARGET_H } from '../render/mobs';

/** 효과별 상한(품질 배율 1). 테스트·문서가 같은 값을 읽는다 */
export const REGION_FX_BUDGET = {
  mist: 64, glint: 96, shard: 64,
  auraMist: 40, auraGlint: 56,
  auraPerSec: 16, auraRange: 22,
  burstsPerTick: 3,
  frostBurst: { shard: 10, glint: 6, mist: 3 },
  poisonBurst: { glint: 10, mist: 5 },
  sandBurst: { shard: 8, glint: 8, mist: 4 },
  voidBurst: { shard: 12, glint: 6, mist: 2 },
  /** 심연 처치: 파편이 출발하는 구 반지름(m, 몸 둘레) — 이 안으로만 빨려 든다 */
  voidRadius: 1.1,
  bossMul: 3,
  /** 보스 처치 안개: 판 수는 일반과 같고(배율 없음) 알파만 낮춘다 — 큰 판이 겹쳐 플레이 영역을 덮지 않게 */
  bossMistAlpha: 0.16,
} as const;

type Blend = 'normal' | 'add';

export class MotePool {
  readonly mesh: THREE.InstancedMesh;
  n = 0;
  readonly px: Float32Array; readonly py: Float32Array; readonly pz: Float32Array;
  readonly vx: Float32Array; readonly vy: Float32Array; readonly vz: Float32Array;
  readonly age: Float32Array; readonly life: Float32Array; readonly w0: Float32Array; readonly w1: Float32Array;
  readonly cr: Float32Array; readonly cg: Float32Array; readonly cb: Float32Array; readonly a0: Float32Array;
  readonly grav: Float32Array; readonly drag: Float32Array; readonly sway: Float32Array; readonly aura: Uint8Array;
  private readonly alpha: THREE.InstancedBufferAttribute;

  constructor(readonly cap: number, blend: Blend, shape: 'disc' | 'dot' | 'diamond', renderOrder: number) {
    const geo = new THREE.PlaneGeometry(1, 1);
    this.alpha = new THREE.InstancedBufferAttribute(new Float32Array(cap), 1);
    this.alpha.setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('aAlpha', this.alpha);
    const mat = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, blending: blend === 'add' ? THREE.AdditiveBlending : THREE.NormalBlending });
    mat.forceSinglePass = true;
    mat.fog = false;
    const p = uv().sub(0.5).mul(2);
    const d = shape === 'diamond' ? p.x.abs().add(p.y.abs().mul(0.6)) : length(vec2(p.x, p.y));
    const edge = shape === 'disc' ? smoothstep(float(1), float(0.15), d) : shape === 'dot' ? smoothstep(float(1), float(0.0), d).pow(1.6) : smoothstep(float(1), float(0.85), d);
    mat.opacityNode = edge.mul(attribute('aAlpha', 'float'));
    this.mesh = new THREE.InstancedMesh(geo, mat, cap);
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.setColorAt(0, new THREE.Color(1, 1, 1));
    (this.mesh.instanceColor as THREE.InstancedBufferAttribute).setUsage(THREE.DynamicDrawUsage);
    this.mesh.frustumCulled = false;
    this.mesh.count = 0;
    this.mesh.renderOrder = renderOrder;
    const f = (): Float32Array => new Float32Array(cap);
    this.px = f(); this.py = f(); this.pz = f(); this.vx = f(); this.vy = f(); this.vz = f();
    this.age = f(); this.life = f(); this.w0 = f(); this.w1 = f();
    this.cr = f(); this.cg = f(); this.cb = f(); this.a0 = f(); this.grav = f(); this.drag = f(); this.sway = f();
    this.aura = new Uint8Array(cap);
  }

  /** 살아 있는 오라 입자 수 */
  auraCount = 0;

  spawn(x: number, y: number, z: number, vx: number, vy: number, vz: number, life: number, w0: number, w1: number, color: number, alpha: number, grav: number, drag: number, sway: number, aura: boolean): boolean {
    if (this.n >= this.cap) return false;
    const i = this.n++;
    this.px[i] = x; this.py[i] = y; this.pz[i] = z; this.vx[i] = vx; this.vy[i] = vy; this.vz[i] = vz;
    this.age[i] = 0; this.life[i] = life; this.w0[i] = w0; this.w1[i] = w1;
    this.cr[i] = (color >> 16 & 255) / 255; this.cg[i] = (color >> 8 & 255) / 255; this.cb[i] = (color & 255) / 255;
    this.a0[i] = alpha; this.grav[i] = grav; this.drag[i] = drag; this.sway[i] = sway; this.aura[i] = aura ? 1 : 0;
    if (aura) this.auraCount++;
    return true;
  }

  private kill(i: number): void {
    if (this.aura[i]) this.auraCount--;
    const j = --this.n;
    if (i === j) return;
    this.px[i] = this.px[j]; this.py[i] = this.py[j]; this.pz[i] = this.pz[j]; this.vx[i] = this.vx[j]; this.vy[i] = this.vy[j]; this.vz[i] = this.vz[j];
    this.age[i] = this.age[j]; this.life[i] = this.life[j]; this.w0[i] = this.w0[j]; this.w1[i] = this.w1[j];
    this.cr[i] = this.cr[j]; this.cg[i] = this.cg[j]; this.cb[i] = this.cb[j]; this.a0[i] = this.a0[j];
    this.grav[i] = this.grav[j]; this.drag[i] = this.drag[j]; this.sway[i] = this.sway[j]; this.aura[i] = this.aura[j];
  }

  /** dt 초 진행 후 카메라 정면 빌보드 행렬(오른쪽 r, 위 u)을 쓴다 */
  update(dt: number, rx: number, ry: number, rz: number, ux: number, uy: number, uz: number, fx: number, fy: number, fz: number, time: number): void {
    const m = this.mesh.instanceMatrix.array as Float32Array;
    const col = this.mesh.instanceColor!.array as Float32Array;
    const al = this.alpha.array as Float32Array;
    let i = 0;
    while (i < this.n) {
      if (dt > 0) {
        this.age[i] += dt;
        if (this.age[i] >= this.life[i]) { this.kill(i); continue; }
        const k = Math.max(0, 1 - this.drag[i] * dt);
        this.vy[i] -= this.grav[i] * dt;
        this.vx[i] *= k; this.vy[i] *= k; this.vz[i] *= k;
        const sw = this.sway[i] * Math.sin(time * 2.1 + i * 1.7) * dt;
        this.px[i] += this.vx[i] * dt + sw; this.py[i] += this.vy[i] * dt; this.pz[i] += this.vz[i] * dt + sw * 0.6;
        if (this.py[i] < 0.02) { this.py[i] = 0.02; this.vy[i] *= -0.25; this.vx[i] *= 0.5; this.vz[i] *= 0.5; }
      }
      const t = this.age[i] / this.life[i];
      const w = this.w0[i] + (this.w1[i] - this.w0[i]) * t;
      // 들어올 때 빠르게, 나갈 때 천천히(덜 깜빡이게)
      const a = this.a0[i] * Math.min(1, t * 6) * (1 - t * t);
      const o = i * 16;
      m[o] = rx * w; m[o + 1] = ry * w; m[o + 2] = rz * w; m[o + 3] = 0;
      m[o + 4] = ux * w; m[o + 5] = uy * w; m[o + 6] = uz * w; m[o + 7] = 0;
      m[o + 8] = fx; m[o + 9] = fy; m[o + 10] = fz; m[o + 11] = 0;
      m[o + 12] = this.px[i]; m[o + 13] = this.py[i]; m[o + 14] = this.pz[i]; m[o + 15] = 1;
      col[i * 3] = this.cr[i]; col[i * 3 + 1] = this.cg[i]; col[i * 3 + 2] = this.cb[i];
      al[i] = a;
      i++;
    }
    this.mesh.count = this.n;
    this.mesh.instanceMatrix.needsUpdate = true;
    this.mesh.instanceColor!.needsUpdate = true;
    this.alpha.needsUpdate = true;
  }

  /** 가장 많이 진행된 입자 하나를 지운다(C2 마물 연출 풀이 가득 차면 오래된 것을 대체) */
  evictOldest(): void {
    if (this.n === 0) return;
    let best = 0, bf = -1;
    for (let i = 0; i < this.n; i++) { const f = this.age[i] / this.life[i]; if (f > bf) { bf = f; best = i; } }
    this.kill(best);
  }

  clear(): void { this.n = 0; this.auraCount = 0; this.mesh.count = 0; }
}


export class RegionMobFx {
  readonly group = new THREE.Group();
  private readonly mist: MotePool;
  private readonly glint: MotePool;
  private readonly shard: MotePool;
  private readonly scale: number;
  private seed = 0x2545f491;
  private auraAcc = 0;
  private auraCursor = 0;
  private burstTick = -1;
  private burstsThisTick = 0;
  private reduceFlash = false;
  private readonly right = new THREE.Vector3();
  private readonly up = new THREE.Vector3();
  private readonly fwd = new THREE.Vector3();
  /** QA: 누적 생성 수 */
  readonly spawned = { aura: 0, burst: 0 };

  constructor(particleScale: number) {
    this.scale = Math.max(0.25, particleScale);
    const c = (n: number): number => Math.max(8, Math.floor(n * this.scale));
    this.mist = new MotePool(c(REGION_FX_BUDGET.mist), 'normal', 'disc', 19);
    this.glint = new MotePool(c(REGION_FX_BUDGET.glint), 'add', 'dot', 22);
    this.shard = new MotePool(c(REGION_FX_BUDGET.shard), 'normal', 'diamond', 20);
    this.group.add(this.mist.mesh, this.shard.mesh, this.glint.mesh);
  }

  private rnd(): number {
    this.seed = (Math.imul(this.seed, 1664525) + 1013904223) >>> 0;
    return this.seed / 4294967296;
  }

  setReduceFlash(on: boolean): void { this.reduceFlash = on; }

  /** 살아 있는 입자 수(QA·예산 테스트) */
  get active(): { mist: number; glint: number; shard: number; auraMist: number; auraGlint: number } {
    return { mist: this.mist.n, glint: this.glint.n, shard: this.shard.n, auraMist: this.mist.auraCount, auraGlint: this.glint.auraCount };
  }

  /** 처치(Ev.Kill)·보스 처치(Ev.BossDefeated): 지역3~6에서만 */
  onEvent(e: GameEvent, mapId: string): void {
    const look = REGION_LOOK[mapId];
    if (!look) return;
    if (e.type === Ev.Kill) {
      if (e.tick !== this.burstTick) { this.burstTick = e.tick; this.burstsThisTick = 0; }
      if (this.burstsThisTick >= REGION_FX_BUDGET.burstsPerTick) return;
      this.burstsThisTick++;
      this.burst(look, e.x, e.z, e.c, (e.a | 0) === 2 ? 1.25 : 1);
    } else if (e.type === Ev.BossDefeated) {
      this.burst(look, e.x, e.z, 0, REGION_FX_BUDGET.bossMul, 2.2);
    }
  }

  /** 처치 폭발: 서리 = 얼음 파편 + 반짝임 + 서리 김 / 독 = 포자 구름 + 빛나는 포자 / 모래 = 금빛 모래 흩어짐 / 심연 = 보라 파편 흡입 */
  burst(look: RegionLook, x: number, z: number, dir: number, mul: number, size = 1): void {
    const B = REGION_FX_BUDGET;
    const glow = this.reduceFlash ? 0.55 : 1;
    // 보스 처치(mul > 1): 안개는 배율 없이 일반 수, 알파는 낮춰 큰 판이 겹쳐도 불투명 구름이 되지 않게
    const boss = mul > 1.5;
    if (look === 'frost') {
      for (let i = 0, n = Math.round(B.frostBurst.shard * mul); i < n; i++) {
        const a = dir + (this.rnd() - 0.5) * 4.2, s = (2.2 + this.rnd() * 3.2) * Math.sqrt(size);
        this.shard.spawn(x, 0.55 + this.rnd() * 0.7 * size, z, Math.sin(a) * s, 2.2 + this.rnd() * 2.8, Math.cos(a) * s, 0.5 + this.rnd() * 0.35,
          (0.1 + this.rnd() * 0.08) * size, 0.05 * size, this.rnd() < 0.5 ? 0xd8f4ff : 0x8fd2f6, 0.95, 14, 1.2, 0, false);
      }
      for (let i = 0, n = Math.round(B.frostBurst.glint * mul); i < n; i++) {
        const a = this.rnd() * Math.PI * 2, s = 0.8 + this.rnd() * 1.6;
        this.glint.spawn(x, 0.7 + this.rnd() * 0.6 * size, z, Math.sin(a) * s, 0.8 + this.rnd(), Math.cos(a) * s, 0.35 + this.rnd() * 0.25, 0.22 * size, 0.06, 0x9fe6ff, 0.9 * glow, 1.5, 2, 0, false);
      }
      for (let i = 0, n = boss ? B.frostBurst.mist : Math.round(B.frostBurst.mist * mul); i < n; i++) {
        const a = this.rnd() * Math.PI * 2;
        this.mist.spawn(x + Math.sin(a) * 0.3, 0.35, z + Math.cos(a) * 0.3, Math.sin(a) * 0.7, 0.3, Math.cos(a) * 0.7, 0.9 + this.rnd() * 0.3, 0.5 * size, 1.25 * size, 0xe6f6ff, boss ? B.bossMistAlpha : 0.42, 0, 1.5, 0, false);
      }
    } else if (look === 'poison') {
      for (let i = 0, n = boss ? B.poisonBurst.mist : Math.round(B.poisonBurst.mist * mul); i < n; i++) {
        const a = this.rnd() * Math.PI * 2, s = 0.5 + this.rnd() * 0.8;
        this.mist.spawn(x + Math.sin(a) * 0.25, 0.5 + this.rnd() * 0.4, z + Math.cos(a) * 0.25, Math.sin(a) * s, 0.45 + this.rnd() * 0.3, Math.cos(a) * s, 1.0 + this.rnd() * 0.4,
          0.45 * size, 1.3 * size, this.rnd() < 0.5 ? 0x8fcf3a : 0x6aa63a, boss ? B.bossMistAlpha : 0.4, -0.1, 1.4, 0.2, false);
      }
      for (let i = 0, n = Math.round(B.poisonBurst.glint * mul); i < n; i++) {
        const a = this.rnd() * Math.PI * 2, s = 0.6 + this.rnd() * 1.4;
        this.glint.spawn(x, 0.7 + this.rnd() * 0.5 * size, z, Math.sin(a) * s, 1.2 + this.rnd() * 1.2, Math.cos(a) * s, 0.9 + this.rnd() * 0.6, 0.11 * size, 0.07, 0xc4ff4a, 0.85 * glow, 0.6, 1.6, 0.5, false);
      }
    } else if (look === 'sand') {
      // 금빛 모래 알갱이가 사방으로 튀었다 무겁게 흘러내리고(중력), 반짝이는 티끌, 낮게 퍼지는 모래 먼지
      for (let i = 0, n = Math.round(B.sandBurst.shard * mul); i < n; i++) {
        const a = dir + (this.rnd() - 0.5) * 5.2, s = (1.4 + this.rnd() * 2.2) * Math.sqrt(size);
        this.shard.spawn(x, 0.5 + this.rnd() * 0.8 * size, z, Math.sin(a) * s, 1.4 + this.rnd() * 2.0, Math.cos(a) * s, 0.55 + this.rnd() * 0.35,
          (0.08 + this.rnd() * 0.06) * size, 0.03 * size, this.rnd() < 0.55 ? 0xf2c860 : 0xd89a34, 0.95, 11, 1.6, 0, false);
      }
      for (let i = 0, n = Math.round(B.sandBurst.glint * mul); i < n; i++) {
        const a = this.rnd() * Math.PI * 2, s = 0.6 + this.rnd() * 1.5;
        this.glint.spawn(x, 0.6 + this.rnd() * 0.7 * size, z, Math.sin(a) * s, 0.5 + this.rnd() * 0.9, Math.cos(a) * s, 0.45 + this.rnd() * 0.35, 0.16 * size, 0.04,
          this.rnd() < 0.7 ? 0xffd66a : 0x5ff0e0, 0.85 * glow, 2.5, 1.8, 0, false);
      }
      for (let i = 0, n = boss ? B.sandBurst.mist : Math.round(B.sandBurst.mist * mul); i < n; i++) {
        const a = this.rnd() * Math.PI * 2;
        this.mist.spawn(x + Math.sin(a) * 0.3, 0.25, z + Math.cos(a) * 0.3, Math.sin(a) * 0.9, 0.18, Math.cos(a) * 0.9, 0.9 + this.rnd() * 0.3, 0.45 * size, 1.2 * size,
          this.rnd() < 0.5 ? 0xe2c48a : 0xc9a46a, boss ? B.bossMistAlpha : 0.36, 0, 1.6, 0.1, false);
      }
    } else {
      // 보라 파편이 몸 둘레 구에서 출발해 처치 지점(가슴 높이)으로 빨려 든다: 속도 = 거리 / 수명, 감쇠·중력 없음 → 수명 끝에 중심에서 사라진다
      const R = B.voidRadius * Math.sqrt(size), cy = 0.75 * Math.min(2, size);
      for (let i = 0, n = Math.round(B.voidBurst.shard * mul); i < n; i++) {
        const a = this.rnd() * Math.PI * 2, el = (this.rnd() - 0.3) * 1.2, r = R * (0.7 + this.rnd() * 0.3);
        const ox = Math.sin(a) * Math.cos(el) * r, oy = Math.sin(el) * r * 0.7, oz = Math.cos(a) * Math.cos(el) * r;
        const life = 0.42 + this.rnd() * 0.18;
        this.shard.spawn(x + ox, Math.max(0.1, cy + oy), z + oz, -ox / life, (cy - Math.max(0.1, cy + oy)) / life, -oz / life, life,
          (0.13 + this.rnd() * 0.06) * size, 0.02 * size, this.rnd() < 0.5 ? 0x9a3cff : 0x3a1260, 0.95, 0, 0, 0, false);
      }
      // 중심 불티: 빨려 든 뒤 위로 사그라진다(작은 점, 섬광 아님)
      for (let i = 0, n = Math.round(B.voidBurst.glint * mul); i < n; i++) {
        const a = this.rnd() * Math.PI * 2, s = 0.2 + this.rnd() * 0.5;
        this.glint.spawn(x, cy, z, Math.sin(a) * s, 0.6 + this.rnd() * 0.8, Math.cos(a) * s, 0.5 + this.rnd() * 0.4, 0.14 * size, 0.04,
          this.rnd() < 0.6 ? 0xd070ff : 0xff5ad8, 0.8 * glow, -0.4, 1.2, 0.2, false);
      }
      for (let i = 0, n = boss ? B.voidBurst.mist : Math.round(B.voidBurst.mist * mul); i < n; i++) {
        this.mist.spawn(x, 0.3, z, 0, 0.25, 0, 0.8 + this.rnd() * 0.3, 1.1 * size, 0.3 * size, 0x2a1240, boss ? B.bossMistAlpha : 0.4, 0, 0, 0.05, false);
      }
    }
    this.spawned.burst++;
  }

  /**
   * 오라: 카메라 가까이 살아 있는 몹 둘레에 순번으로 입자를 하나씩 띄운다(전체 초당 상한). 지역3~6이 아니면 아무것도 하지 않는다.
   * dt = 세계 시계 초(일시정지·히트스톱 0, 각성 감속 중 느리게) — 몹 클립과 같은 시계라 멈춘 몹 둘레 안개도 같이 멈춘다.
   */
  update(view: GameView, dt: number, camera: THREE.Camera, time: number): void {
    const look = REGION_LOOK[view.map.id];
    const B = REGION_FX_BUDGET;
    if (look && dt > 0) {
      const m = view.mobs;
      this.auraAcc = Math.min(4, this.auraAcc + dt * B.auraPerSec * this.scale);
      const cx = camera.position.x, cz = camera.position.z;
      let tries = 0;
      while (this.auraAcc >= 1 && tries < m.capacity) {
        tries++;
        const i = this.auraCursor;
        this.auraCursor = (this.auraCursor + 1) % m.capacity;
        const st = m.state[i];
        if (st === MobState.Inactive || st === MobState.Dying || st === MobState.Spawning) continue;
        if (Math.hypot(m.x[i] - cx, m.z[i] - cz) > B.auraRange) continue;
        this.auraAcc -= 1;
        this.aura(look, m.x[i], m.z[i], TARGET_H[m.kind[i]] ?? 1.4);
      }
    }
    camera.matrixWorld.extractBasis(this.right, this.up, this.fwd);
    const r = this.right, u = this.up, f = this.fwd;
    this.mist.update(dt, r.x, r.y, r.z, u.x, u.y, u.z, f.x, f.y, f.z, time);
    this.glint.update(dt, r.x, r.y, r.z, u.x, u.y, u.z, f.x, f.y, f.z, time);
    this.shard.update(dt, r.x, r.y, r.z, u.x, u.y, u.z, f.x, f.y, f.z, time);
  }

  private aura(look: RegionLook, x: number, z: number, h: number): void {
    const B = REGION_FX_BUDGET;
    const glow = this.reduceFlash ? 0.55 : 1;
    const a = this.rnd() * Math.PI * 2, r = 0.25 + this.rnd() * 0.35;
    if (look === 'frost') {
      // 발밑 서리 안개(낮게 깔려 천천히 퍼진다) 3 : 몸 둘레 얼음 반짝임 1
      if (this.rnd() < 0.75) {
        if (this.mist.auraCount < B.auraMist * this.scale && this.mist.spawn(x + Math.sin(a) * r, 0.12, z + Math.cos(a) * r, Math.sin(a) * 0.18, 0.06, Math.cos(a) * 0.18, 1.6 + this.rnd() * 0.6, 0.35, 0.85, 0xdcefff, 0.2, 0, 0.3, 0.05, true)) this.spawned.aura++;
      } else if (this.glint.auraCount < B.auraGlint * this.scale && this.glint.spawn(x + Math.sin(a) * r * 1.2, h * (0.4 + this.rnd() * 0.6), z + Math.cos(a) * r * 1.2, 0, 0.12, 0, 0.5 + this.rnd() * 0.3, 0.16, 0.04, 0xbff0ff, 0.8 * glow, 0, 0.5, 0, true)) this.spawned.aura++;
    } else if (look === 'poison') {
      // 갓·등에서 피어오르는 빛나는 포자 2 : 발밑 독안개 1
      if (this.rnd() < 0.66) {
        if (this.glint.auraCount < B.auraGlint * this.scale && this.glint.spawn(x + Math.sin(a) * r * 0.7, h * (0.65 + this.rnd() * 0.35), z + Math.cos(a) * r * 0.7, 0, 0.28 + this.rnd() * 0.18, 0, 1.4 + this.rnd() * 0.6, 0.075, 0.05, this.rnd() < 0.6 ? 0xc4ff4a : 0x9cff6a, 0.75 * glow, -0.02, 0.2, 0.35, true)) this.spawned.aura++;
      } else if (this.mist.auraCount < B.auraMist * this.scale && this.mist.spawn(x + Math.sin(a) * r, 0.15, z + Math.cos(a) * r, Math.sin(a) * 0.12, 0.1, Math.cos(a) * 0.12, 1.8 + this.rnd() * 0.6, 0.4, 0.9, 0x7fb23a, 0.16, 0, 0.3, 0.08, true)) this.spawned.aura++;
    } else if (look === 'sand') {
      // 발밑을 한쪽으로 쓸고 가는 모래 바람 2 : 몸 둘레 금빛 티끌 1
      if (this.rnd() < 0.66) {
        if (this.mist.auraCount < B.auraMist * this.scale && this.mist.spawn(x + Math.sin(a) * r, 0.1, z + Math.cos(a) * r, 0.45 + this.rnd() * 0.2, 0.03, 0.12, 1.3 + this.rnd() * 0.5, 0.3, 0.8, 0xdcbf86, 0.18, 0, 0.4, 0.04, true)) this.spawned.aura++;
      } else if (this.glint.auraCount < B.auraGlint * this.scale && this.glint.spawn(x + Math.sin(a) * r * 1.2, h * (0.2 + this.rnd() * 0.6), z + Math.cos(a) * r * 1.2, 0.3, 0.05, 0.08, 0.7 + this.rnd() * 0.4, 0.09, 0.03, this.rnd() < 0.75 ? 0xffd27a : 0x6ff2e2, 0.7 * glow, 0, 0.4, 0.2, true)) this.spawned.aura++;
    } else {
      // 발밑 그림자 연기 1 : 위로 떠오르는 보라 불티 1
      if (this.rnd() < 0.5) {
        if (this.mist.auraCount < B.auraMist * this.scale && this.mist.spawn(x + Math.sin(a) * r, 0.12, z + Math.cos(a) * r, Math.sin(a) * 0.1, 0.16, Math.cos(a) * 0.1, 1.5 + this.rnd() * 0.5, 0.35, 0.8, 0x24102e, 0.22, 0, 0.3, 0.06, true)) this.spawned.aura++;
      } else if (this.glint.auraCount < B.auraGlint * this.scale && this.glint.spawn(x + Math.sin(a) * r * 0.8, h * (0.1 + this.rnd() * 0.4), z + Math.cos(a) * r * 0.8, 0, 0.35 + this.rnd() * 0.2, 0, 1.1 + this.rnd() * 0.5, 0.08, 0.03, this.rnd() < 0.7 ? 0xb45cff : 0xff5ad8, 0.75 * glow, -0.05, 0.2, 0.3, true)) this.spawned.aura++;
    }
  }

  /** 셰이더 미리 컴파일용 */
  prewarm(on: boolean): void {
    for (const p of [this.mist, this.glint, this.shard]) p.mesh.count = on ? 1 : 0;
  }

  clear(): void { this.mist.clear(); this.glint.clear(); this.shard.clear(); this.auraAcc = 0; }

  dispose(): void {
    for (const p of [this.mist, this.glint, this.shard]) { p.mesh.geometry.dispose(); (p.mesh.material as THREE.Material).dispose(); p.mesh.dispose(); }
  }
}
